/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import { toJS } from 'mobx';
import Framework from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';

const AccessEntry = types.model('AccessEntry', {
  email: types.string,
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  accessType: types.enumeration(['view', 'edit']),
  sharedBy: types.maybeNull(types.string),
  sharedAt: types.maybeNull(types.string)
});

const Owner = types.model('Owner', {
  email: types.string,
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string)
});

const GraphAccessModel = types
  .model('GraphAccessModel', {
    accessList: types.optional(types.array(AccessEntry), []),
    owner: types.maybeNull(Owner),
    error: types.maybeNull(types.string),
    updateSuccess: types.optional(types.boolean, false)
  })
  .views((self) => ({
    get accessData() {
      return {
        accessList: toJS(self.accessList),
        owner: self.owner ? toJS(self.owner) : null,
        error: self.error,
        updateSuccess: self.updateSuccess
      };
    }
  }))
  .actions((self) => ({
    // Fetch the access list for a graph
    getGraphAccess(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        error: null
      });
      Framework.getEventManager().publish(constants.GET_GRAPH_ACCESS, { payload });
    },

    // Update user access for a graph
    updateGraphAccess(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        error: null,
        updateSuccess: false
      });
      Framework.getEventManager().publish(constants.UPDATE_GRAPH_ACCESS, { payload });
    },

    // Revoke user access for a graph
    revokeGraphAccess(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        error: null
      });
      Framework.getEventManager().publish(constants.REVOKE_GRAPH_ACCESS, { payload });
    },

    optimisticUpdateAccess(email, newAccessType) {
      const currentList = toJS(self.accessList);
      const updatedList = currentList.map((user) =>
        user.email === email ? { ...user, accessType: newAccessType } : user
      );
      applySnapshot(self.accessList, updatedList);
    },

    optimisticRemoveAccess(email) {
      const currentList = toJS(self.accessList);
      const updatedList = currentList.filter((user) => user.email !== email);
      applySnapshot(self.accessList, updatedList);
    },

    // Optimistically update entire access list (for rollback on error)
    optimisticUpdateAccessList(newList) {
      applySnapshot(self, {
        ...getSnapshot(self),
        accessList: newList,
        error: null
      });
    },

    // Set the access list response - accepts raw API response data
    setAccessList(data) {
      // Transform backend response (sharedUsers) to model format (accessList)
      const accessList = (data.sharedUsers || []).map((user) => ({
        email: user.email,
        firstName: user.firstName || null,
        lastName: user.lastName || null,
        accessType: user.accessType,
        sharedBy: user.sharedBy,
        sharedAt: user.sharedDate || user.lastUpdated
      }));

      // Parse owner object from response
      const owner = data.owner
        ? {
            email: data.owner.email,
            firstName: data.owner.firstName || null,
            lastName: data.owner.lastName || null
          }
        : null;

      applySnapshot(self, {
        ...getSnapshot(self),
        accessList,
        owner
      });
      Framework.getEventManager().publish(constants.GET_GRAPH_ACCESS_SUCCESS, { accessList, owner });
    },

    // Set the update response - accepts raw API response data
    setUpdateSuccess(data) {
      applySnapshot(self, {
        ...getSnapshot(self),
        updateSuccess: true
      });
      // Update operations don't return the full access list (optimistic updates handle UI)
      // Just extract the message for event publishing
      const response = {
        message: data.message || 'Access updated successfully'
      };
      Framework.getEventManager().publish(constants.UPDATE_GRAPH_ACCESS_SUCCESS, response);
    },

    // Set the revoke response - accepts raw API response data
    setRevokeSuccess(data) {
      // Extract relevant data for event publishing
      const response = {
        message: data.message || 'Access revoked successfully',
        email: data.email
      };
      Framework.getEventManager().publish(constants.REVOKE_GRAPH_ACCESS_SUCCESS, response);
    },

    // Set error for any operation
    setError(errorMessage, operation = 'get') {
      const snapshot = getSnapshot(self);
      if (operation === 'update') {
        applySnapshot(self, {
          ...snapshot,
          updateSuccess: false,
          error: errorMessage
        });
        Framework.getEventManager().publish(constants.UPDATE_GRAPH_ACCESS_ERROR, { error: errorMessage });
      } else if (operation === 'revoke') {
        applySnapshot(self, {
          ...snapshot,
          error: errorMessage
        });
        Framework.getEventManager().publish(constants.REVOKE_GRAPH_ACCESS_ERROR, { error: errorMessage });
      } else {
        applySnapshot(self, {
          ...snapshot,
          error: errorMessage
        });
        Framework.getEventManager().publish(constants.GET_GRAPH_ACCESS_ERROR, { error: errorMessage });
      }
    },

    // Reset state
    resetState() {
      applySnapshot(self, {
        accessList: [],
        owner: null,
        error: null,
        updateSuccess: false
      });
    }
  }));

export default GraphAccessModel;
